import { notification } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { usePermissions } from "src/pages/features/usePermissions";
import dayjs, { Dayjs } from "dayjs";
import FanControl from "./FanControl";
import MoveGrainControl from "./MoveGrainControl";
import { HubConnectionBuilder, HubConnectionState, HubConnection, LogLevel } from "@microsoft/signalr";
import { useGetPremierPlusBinDto } from "src/queries/useGetPremierPlusBinDto";
import { useQueryClient } from "@tanstack/react-query";

const ControlPanel = () => {

    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const permissions = usePermissions();
    const binInfo = {
        name: "Test_PremierPlus_Physical_1", 
        id:1107
    };
    const lastTapestripTime = dayjs("2024-12-09T15:30:00");

    const [activeTabKey, setActiveTabKey] = useState("1");
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [showTapestripLog, setShowTapestripLog] = useState(false);

    const [tapestripHistoryModalRange, setTapestripHistoryModalRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const  { getPremierPlusBinDtoQueryKey, getPremierPlusBinDtoQuery } = useGetPremierPlusBinDto(binInfo?.id, { enabled: true, refetchOnWindowFocus: false });

    useEffect( () => {

        if(getPremierPlusBinDtoQuery.data == null){
            notification.error({ message:'Could not fetch snapshot' });
        }else{
            notification.success({ message: 'New Message received' });
        }
    }, [getPremierPlusBinDtoQuery.dataUpdatedAt]);

    const startSignalRConnection = useCallback(async (connection: HubConnection) => {
        try {
            await connection.start();
            console.log("signalR connected, ", "url: ", connection.baseUrl);
        } catch (err) {
            console.error("Error establishing signalr connection: ", err);
            setTimeout(() => startSignalRConnection(connection), 5000);
        }
    }, []);
    
    useEffect(() => {
        const binId = binInfo?.id;
        if (binId == null) {
            return;
        }
        let connection: HubConnection;
        let disconnectedDueToUnmount = false;
    
        const setUpSignalRConnection = async (binId: number) => {

            const connection = new HubConnectionBuilder()
                .withUrl("/signalr")
                .withAutomaticReconnect()
                .configureLogging(LogLevel.Information)
                .build();

                connection.on("NewSnapshot", async (message: any) => {
                    queryClient.invalidateQueries({ queryKey:[getPremierPlusBinDtoQueryKey] });
                    console.log('Requesting new snapshot');
                });

                // start the connection
                await startSignalRConnection(connection);
                // subscribe to bin

                if (connection.state === HubConnectionState.Connected) {
                    try {
                        await connection.invoke("SubscribeBin", binId);
                        console.log("signalr: subscribed to binId: ", binInfo?.id);
                    } catch (err) {
                        console.error(`signalr: Error subscribing to bin with Id ${binId}`, err.toString());
                    }
                } else {
                    console.log("hub not connected");
                }
    
                // handle disconnect
                connection.onclose(async error => {
                    console.log("disconnected from SignalR", error);
                    if (disconnectedDueToUnmount) {
                        console.debug("skipping reconnect due to component cleanup/umount");
                        return;
                    }
                    await startSignalRConnection(connection);
                });
    
                //return the connection
                return connection;
            };
    
            if (binId) {
                setUpSignalRConnection(binId).then(con => {
                    connection = con;
                });
            }
    
            const cleanUpSignalRConnection = async (binId: number, connection: HubConnection) => {
                if (connection.state === HubConnectionState.Connected) {
                    connection;
                    try {
                        await connection.invoke("UnsubscribeBin", binId);
                        console.log("unsubscribing from bin", binId);
                    } catch (err) {
                        return console.error("Error unsubscribing to bin", binId, err.toString());
                    }
                    connection.off("SubscribeBin");
                    disconnectedDueToUnmount = true;
                    connection.stop();
                } else {
                    connection.off("SubscribeBin");
                    disconnectedDueToUnmount = true;
                    connection.stop();
                }
            };
    
            return function cleanUp() {
                if (binInfo?.id) {
                    const binId = binInfo?.id;
                    cleanUpSignalRConnection(binId, connection).catch(err => console.log("failed to cleanup signalr", err));
                }
            };
        }, [binInfo?.id]);

    return (
        <>
            {/* <Row justify={"space-between"} align="middle">
                <Col>
                    <PageHeader
                        style={{ width: "100%", paddingRight: 16, paddingLeft: 10 }}
                        onBack={() => {
                            // key unique to the location, only present if we came to this page from another first using React Router. ex. clicking details in BinDetails.
                            // https://reactrouter.com/en/main/start/concepts#locations
                            const hasHistory = !(location?.key == null);
                            if (!hasHistory) {
                                // going to the homepage is the best we can do currently (no knowledge of prior history or upper route/component to go back to)
                                history.push(Routes.HOME_ROUTE);
                            } else {
                                history.goBack();
                            }
                        }}
                        title={binInfo.name}
                        tags={[]}
                    />
                </Col>
                <Col>
                    <Typography.Text style={{ textAlign: "center", verticalAlign: "center" }} type="secondary">{`Last Tape Reading: ${
                        lastTapestripTime != null ? lastTapestripTime?.format("MM/DD/YYYY h:mm a") : "----"
                    }`}</Typography.Text>
                </Col>
                <Col style={{ marginLeft: "auto" }}>
                    <Button size="small" icon={<RedoOutlined />} type="primary" />
                </Col>
            </Row>
            <Modal
                title={
                    <>
                        <Flex gap="large" align="center" wrap="wrap">
                            <Typography.Text>Tapestrip</Typography.Text>
                            <DatePicker.RangePicker
                                allowClear
                                onChange={(dates, dateStrings) => {
                                    const userTimezone = dayjs.tz.guess();

                                    let startOf = null;
                                    let endOf = null;
                                    if (dates?.[0] != null) {
                                        const convertedStart = dayjs.tz(dates[0], userTimezone);
                                        startOf = dayjs.tz(convertedStart?.startOf?.("day"));
                                    }
                                    if (dates?.[1] != null) {
                                        const convertedEnd = dayjs.tz(dates[1], userTimezone);
                                        endOf = convertedEnd?.endOf?.("day");
                                    }

                                    setTapestripHistoryModalRange([startOf, endOf]);
                                }}
                                showTime={false}
                            />
                        </Flex>
                    </>
                }
                styles={{ body: { overflowY: "auto", maxHeight: "calc(100vh - 200px)" } }}
                width={"60%"}
                footer={null}
                open={showTapestripLog}
                onOk={() => {
                    setShowTapestripLog(false);
                }}
                onCancel={() => {
                    setShowTapestripLog(false);
                }}
            >
                <PremierPlusTapeStrip range={tapestripHistoryModalRange} />
            </Modal>

            <Modal
                title="Alerts"
                styles={{ body: { overflowY: "auto", maxHeight: "calc(100vh - 172px)" } }}
                width={"85%"}
                footer={null}
                open={showAlertModal}
                onOk={() => {
                    setShowAlertModal(false);
                }}
                onCancel={() => {
                    setShowAlertModal(false);
                }}
            >
                <PremierPlusAlertTable />
            </Modal>
            <Row justify={"space-around"} gutter={[16, 16]}>
                <Col
                    xs={24}
                    xl={8}
                    style={{
                        justifyContent: "center"
                    }}
                >
                    <Flex flex="wrap" justify={"center"}>
                        <Card
                            title="Last Tape Reading"
                            extra={
                                <Flex gap="small">
                                    <Button
                                        onClick={() => {
                                            setShowTapestripLog(true);
                                        }}
                                    >
                                        Tapestrip
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowAlertModal(true);
                                        }}
                                    >
                                        Alerts
                                    </Button>
                                </Flex>
                            }
                            style={{
                                width: "100%"
                            }}
                            styles={{
                                body: {
                                    padding: 0
                                }
                            }}
                        >
                            <Overview layout="horizontal" showLastTapeReadingDate={true} />
                        </Card>
                    </Flex>
                </Col>
                <Col xs={24} xl={8}>
                    <Flex justify={"center"} flex="wrap">
                        <ReadingOnOffDisplay />
                    </Flex>
                </Col>
                <Col xs={24} xl={8}>
                    <Flex justify={"center"}>
                        <DryerSettingsCard />
                    </Flex>
                </Col>
            </Row> */}

            {/* {permissions.canWrite && <Row>
                <Col span={24}>
                    <SettingsEdit />
                </Col>
            </Row>} */}
            
            <br/>
            
            <FanControl />

            <br/>
            
            <MoveGrainControl />

            <br/>

            {/* <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Charts />
                </Col>
            </Row>  */}
        </>
    );
};

export default ControlPanel;